// Podpis prstem na displej — důkazní materiál k odchylkám odvodu a předání
// materiálu (portál F3). Nejdřív se zadá JMÉNO podepisující osoby, pak podpis.
// Výstup = PNG data-URL (ukládá se s časem, jménem a GPS na serveru).

import { useEffect, useRef, useState } from "react";

export interface SignatureResult {
  jmeno: string;
  podpis: string; // PNG data-URL
}

export function SignaturePad({ title, subtitle, onConfirm, onCancel }: {
  title: string;
  subtitle?: string;
  onConfirm: (r: SignatureResult) => void;
  onCancel: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingRef = useRef(false);
  const [jmeno, setJmeno] = useState("");
  const [hasStrokes, setHasStrokes] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Interní rozlišení = CSS velikost × devicePixelRatio, ať podpis není rozmazaný.
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d")!;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = "#14213d";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pos = (e: React.PointerEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const down = (e: React.PointerEvent) => {
    e.preventDefault();
    drawingRef.current = true;
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    // Tečka i při pouhém ťuknutí
    ctx.lineTo(p.x + 0.1, p.y + 0.1);
    ctx.stroke();
    setHasStrokes(true);
  };
  const move = (e: React.PointerEvent) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const up = () => { drawingRef.current = false; };

  const clear = () => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasStrokes(false);
  };

  const confirm = () => {
    if (!jmeno.trim() || !hasStrokes) return;
    // JPEG by ztratil ostré linky; PNG podpisu má ~5–20 kB — v pohodě do eventu.
    onConfirm({ jmeno: jmeno.trim(), podpis: canvasRef.current!.toDataURL("image/png") });
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(13, 20, 36, 0.6)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div style={{
        background: "rgb(var(--c-surface))", borderRadius: 16, padding: 18,
        width: "min(440px, 94vw)", boxSizing: "border-box",
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "rgb(var(--c-text))" }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 13, color: "rgb(var(--c-text-soft))", marginTop: 3 }}>{subtitle}</div>
        )}
        <input
          value={jmeno}
          onChange={(e) => setJmeno(e.target.value)}
          placeholder="Jméno podepisující osoby"
          style={{
            width: "100%", boxSizing: "border-box", margin: "12px 0 10px",
            padding: "12px 14px", fontSize: 16,
            background: "rgb(var(--c-elevated))",
            border: "1.5px solid rgb(var(--c-border-strong))",
            borderRadius: 10, color: "rgb(var(--c-text))", outline: "none",
          }}
        />
        <canvas
          ref={canvasRef}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerLeave={up}
          style={{
            width: "100%", height: 150, display: "block",
            background: "#fff", borderRadius: 10,
            border: "2px dashed rgb(var(--c-border-strong))",
            touchAction: "none",
          }}
        />
        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <button onClick={clear} style={{
            flex: 1, padding: "13px 10px", borderRadius: 10, fontSize: 14, fontWeight: 600,
            background: "transparent", border: "1.5px solid rgb(var(--c-border-strong))",
            color: "rgb(var(--c-text-soft))", cursor: "pointer",
          }}>Smazat</button>
          <button onClick={onCancel} style={{
            flex: 1, padding: "13px 10px", borderRadius: 10, fontSize: 14, fontWeight: 600,
            background: "transparent", border: "1.5px solid rgb(var(--c-border-strong))",
            color: "rgb(var(--c-text-soft))", cursor: "pointer",
          }}>Zrušit</button>
          <button onClick={confirm} disabled={!jmeno.trim() || !hasStrokes} style={{
            flex: 1.4, padding: "13px 10px", borderRadius: 10, fontSize: 14, fontWeight: 700,
            background: "rgb(var(--c-success))", border: "none", color: "#fff",
            opacity: !jmeno.trim() || !hasStrokes ? 0.4 : 1,
            cursor: !jmeno.trim() || !hasStrokes ? "not-allowed" : "pointer",
          }}>Potvrdit podpis</button>
        </div>
      </div>
    </div>
  );
}
